<template>
  <section class="configure-ws-adv">
    <div class="header">
      <h2>Configurações avançadas do turno</h2>
      <p>
        Configure cálculo (saldo não compensatório) e notificações em massa
        para os turnos da empresa.
      </p>
    </div>

    <div v-if="loading" class="loading-block">Carregando turnos...</div>

    <div v-if="!loading">
      <!-- Resumo da empresa -->
      <div class="box">
        <label class="box-title">Configuração atual da empresa</label>
        <ul class="summary-list">
          <li>
            <strong>{{ labels.workShift.notifyWhenExtraHours }}:</strong>
            {{ helpers.formatBool(companySummary.notifyWhenExtraHours) }}
            <span v-if="companySummary.notifyWhenExtraHours">({{ companySummary.notifyExtraHoursInMinutes }} min)</span>
          </li>
          <li>
            <strong>{{ labels.workShift.notifyWhenWorkscheduleChange }}:</strong>
            {{ helpers.formatBool(companySummary.notifyWhenWorkscheduleChange) }}
          </li>
        </ul>
      </div>

      <!-- Cálculo e notificações -->
      <div class="box">
        <label class="box-title">Cálculo e notificações</label>

        <div class="form-row checkbox-line">
          <label>
            <input type="checkbox" v-model="form.hasNoCompensatoryCalculation" />
            {{ labels.workShift.hasNoCompensatoryCalculation }}
          </label>
          <small class="hint">Extra e faltante não se anulam no dia — cada um acumula em separado. (Aplicado só nos turnos)</small>
        </div>

        <div class="form-row checkbox-line">
          <label>
            <input type="checkbox" v-model="form.ignoreNightshiftWhenHasNocturnalExtra" />
            {{ labels.workShift.ignoreNightshiftWhenHasNocturnalExtra }}
          </label>
          <small class="hint">Aplicado só nos turnos.</small>
        </div>

        <div class="form-row checkbox-line">
          <label>
            <input type="checkbox" v-model="form.notifyWhenExtraHours" />
            {{ labels.workShift.notifyWhenExtraHours }}
          </label>
        </div>
        <div class="form-row" v-if="form.notifyWhenExtraHours">
          <label>{{ labels.workShift.notifyExtraHoursInMinutes }}</label>
          <input type="number" min="0" step="1" v-model.number="form.notifyExtraHoursInMinutes" class="form-control" placeholder="Ex: 15" />
        </div>

        <div class="form-row checkbox-line">
          <label>
            <input type="checkbox" v-model="form.notifyWhenHasNoInterval" />
            {{ labels.workShift.notifyWhenHasNoInterval }}
          </label>
        </div>
        <div class="form-row" v-if="form.notifyWhenHasNoInterval">
          <label>{{ labels.workShift.notifyHasNoIntervalInMinutes }}</label>
          <input type="number" min="0" step="1" v-model.number="form.notifyHasNoIntervalInMinutes" class="form-control" placeholder="Ex: 60" />
        </div>

        <div class="form-row checkbox-line">
          <label>
            <input type="checkbox" v-model="form.notifyWhenWorkscheduleChange" />
            {{ labels.workShift.notifyWhenWorkscheduleChange }}
          </label>
        </div>
      </div>

      <!-- Aplicar também na empresa -->
      <div class="box">
        <div class="form-row checkbox-line">
          <label>
            <input type="checkbox" v-model="form.alsoApplyToCompanySettings" />
            Aplicar também na configuração da empresa
          </label>
          <small class="hint">
            Na empresa são aplicadas apenas as notificações. "Não compensatório"
            e "Ignorar adicional noturno..." existem só no turno.
          </small>
        </div>
      </div>

      <!-- Turnos -->
      <div class="box">
        <label class="box-title">
          Turnos
          <span class="select-all">
            <input type="checkbox" :checked="allSelected" @change="toggleAll($event.target.checked)" />
            Selecionar todos
          </span>
        </label>

        <div class="workshift-search">
          <input type="text" v-model="workshiftFilter" placeholder="Buscar turno pelo nome..." />
          <span class="count">{{ filteredWorkshifts.length }} de {{ workshifts.length }} turno(s)</span>
        </div>

        <table class="table-workshifts">
          <thead>
            <tr>
              <th></th>
              <th>{{ labels.workShift.name }}</th>
              <th>{{ labels.workShift.workShiftType }}</th>
              <th>Não comp.</th>
              <th>Notif. HE</th>
              <th>Notif. escala</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="ws in filteredWorkshifts" :key="ws.id">
              <td><input type="checkbox" :value="ws.id" v-model="selectedWorkshifts" /></td>
              <td>{{ ws.name }}</td>
              <td>{{ helpers.getWorkShiftTypeLabel(ws.workShiftType) }}</td>
              <td>{{ helpers.formatBool(ws.hasNoCompensatoryCalculation) }}</td>
              <td>{{ helpers.formatBool(ws.notifyWhenExtraHours) }}</td>
              <td>{{ helpers.formatBool(ws.notifyWhenWorkscheduleChange) }}</td>
            </tr>
            <tr v-if="!filteredWorkshifts.length">
              <td colspan="6" class="empty-row">Nenhum turno encontrado para "{{ workshiftFilter }}".</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="actions">
        <button class="btn-cancel" @click="$router.go(-1)">Voltar</button>
        <button class="btn-primary" :disabled="!canSave || saving" @click="save()">
          <span v-if="!saving">Aplicar configurações</span>
          <span v-else>Aplicando...</span>
        </button>
      </div>
    </div>
  </section>
</template>

<script>
import {
  GET_WORKSHIFT_FULL_VIEW,
  APPLY_WORKSHIFT_ADVANCED_BULK
} from "../../../../urlConfig";
import { REQUEST_SERVICE } from "../../../../requestConfig";
import {
  workShiftLabels,
  labelHelpers
} from "../../../../labels/workshiftLabels";
import swal from "sweetalert";

export default {
  name: "configureWorkshiftAdvanced",
  data() {
    return {
      loading: false,
      saving: false,
      companyId: "",
      companySummary: {},
      workshifts: [],
      selectedWorkshifts: [],
      workshiftFilter: "",
      form: {
        hasNoCompensatoryCalculation: false,
        ignoreNightshiftWhenHasNocturnalExtra: false,
        notifyWhenExtraHours: false,
        notifyExtraHoursInMinutes: 0,
        notifyWhenHasNoInterval: false,
        notifyHasNoIntervalInMinutes: 0,
        notifyWhenWorkscheduleChange: false,
        alsoApplyToCompanySettings: false
      },
      labels: {
        workShift: workShiftLabels
      },
      helpers: labelHelpers
    };
  },
  computed: {
    filteredWorkshifts() {
      const q = (this.workshiftFilter || "").trim().toLowerCase();
      if (!q) return this.workshifts;
      return this.workshifts.filter((w) => (w.name || "").toLowerCase().indexOf(q) !== -1);
    },
    allSelected() {
      const list = this.filteredWorkshifts;
      if (!list.length) return false;
      return list.every((w) => this.selectedWorkshifts.indexOf(w.id) !== -1);
    },
    canSave() {
      return this.selectedWorkshifts.length > 0 || this.form.alsoApplyToCompanySettings;
    }
  },
  beforeMount() {
    this.companyId = this.$route.params.idCompany;
    this.load();
  },
  methods: {
    load() {
      this.loading = true;
      REQUEST_SERVICE.requestContext(
        "GET",
        `${GET_WORKSHIFT_FULL_VIEW}?companyId=${this.companyId}`,
        this.$route.params.context
      ).then((response) => {
          const data = response.data || {};
          this.companySummary = {
            notifyWhenExtraHours: !!data.notifyWhenExtraHours,
            notifyExtraHoursInMinutes: data.notifyExtraHoursInMinutes || 0,
            notifyWhenHasNoInterval: !!data.notifyWhenHasNoInterval,
            notifyHasNoIntervalInMinutes: data.notifyHasNoIntervalInMinutes || 0,
            notifyWhenWorkscheduleChange: !!data.notifyWhenWorkscheduleChange
          };
          this.workshifts = Array.isArray(data.workshifts) ? data.workshifts : [];
          // Pré-preenche o form com os defaults da empresa.
          this.form.notifyWhenExtraHours = !!data.notifyWhenExtraHours;
          this.form.notifyExtraHoursInMinutes = data.notifyExtraHoursInMinutes || 0;
          this.form.notifyWhenHasNoInterval = !!data.notifyWhenHasNoInterval;
          this.form.notifyHasNoIntervalInMinutes = data.notifyHasNoIntervalInMinutes || 0;
          this.form.notifyWhenWorkscheduleChange = !!data.notifyWhenWorkscheduleChange;
          this.loading = false;
        })
        .catch(() => {
          this.loading = false;
          swal("Aviso", "Falha ao carregar dados da empresa.", "error");
        });
    },
    toggleAll(checked) {
      const filteredIds = this.filteredWorkshifts.map((w) => w.id);
      if (checked) {
        const set = {};
        this.selectedWorkshifts.forEach((id) => { set[id] = true; });
        filteredIds.forEach((id) => { set[id] = true; });
        this.selectedWorkshifts = Object.keys(set);
      } else {
        const remove = {};
        filteredIds.forEach((id) => { remove[id] = true; });
        this.selectedWorkshifts = this.selectedWorkshifts.filter((id) => !remove[id]);
      }
    },
    save() {
      if (this.saving || !this.canSave) return;
      this.saving = true;

      const f = this.form;
      const payload = {
        companyId: this.companyId,
        workshiftIds: this.selectedWorkshifts,
        alsoApplyToCompanySettings: f.alsoApplyToCompanySettings,
        hasNoCompensatoryCalculation: f.hasNoCompensatoryCalculation,
        ignoreNightshiftWhenHasNocturnalExtra: f.ignoreNightshiftWhenHasNocturnalExtra,
        notifyWhenExtraHours: f.notifyWhenExtraHours,
        notifyExtraHoursInMinutes: f.notifyWhenExtraHours ? (f.notifyExtraHoursInMinutes || 0) : 0,
        notifyWhenHasNoInterval: f.notifyWhenHasNoInterval,
        notifyHasNoIntervalInMinutes: f.notifyWhenHasNoInterval ? (f.notifyHasNoIntervalInMinutes || 0) : 0,
        notifyWhenWorkscheduleChange: f.notifyWhenWorkscheduleChange
      };

      REQUEST_SERVICE.requestContext("PUT", APPLY_WORKSHIFT_ADVANCED_BULK, this.$route.params.context, payload)
        .then(() => {
          this.saving = false;
          swal("Sucesso", "Configurações avançadas aplicadas em massa.", "success");
          this.load();
        })
        .catch(() => {
          this.saving = false;
          swal("Aviso", "Falha ao aplicar as configurações.", "error");
        });
    }
  }
};
</script>

<style lang="scss" scoped>
.configure-ws-adv {
  padding: 24px;

  .header {
    margin-bottom: 24px;
    h2 { font-weight: bold; margin-bottom: 8px; }
    p { color: #666; font-size: 13px; }
  }

  .loading-block { text-align: center; padding: 40px; color: #888; }

  .box {
    background: #fff; border-radius: 6px; padding: 18px;
    margin-bottom: 18px; box-shadow: 0 1px 3px rgba(0,0,0,0.06);

    .box-title {
      display: flex; justify-content: space-between; align-items: center;
      font-weight: 600; margin-bottom: 12px;
      .select-all { font-weight: normal; font-size: 13px; color: #555; input { margin-right: 4px; } }
    }
  }

  .summary-list { list-style: none; padding: 0; margin: 0; li { padding: 4px 0; font-size: 13px; } }

  .form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }

  .form-row {
    display: flex; flex-direction: column; gap: 4px; margin-bottom: 12px;
    label { font-weight: 500; font-size: 13px; }
    .form-control {
      border: 1px solid #ccc; border-radius: 4px; padding: 6px 10px;
      max-width: 320px;
    }
    .hint { color: #888; font-size: 12px; }
  }
  .checkbox-line label { flex-direction: row; align-items: center; gap: 6px; font-weight: 500; }
  .hint { color: #888; font-size: 12px; }

  .workshift-search {
    display: flex; align-items: center; gap: 10px; margin-bottom: 10px; flex-wrap: wrap;
    input {
      flex: 1 1 260px; min-width: 200px;
      border: 1px solid #ccc; border-radius: 4px; padding: 6px 10px; font-size: 13px;
    }
    .count { font-size: 12px; color: #888; }
  }
  .empty-row { text-align: center; color: #999; padding: 14px; font-style: italic; }

  .table-workshifts {
    width: 100%; border-collapse: collapse; font-size: 13px;
    th, td { border-bottom: 1px solid #eee; padding: 8px 6px; text-align: left; }
    th { color: #555; font-weight: 600; background: #fafafa; }
  }

  .actions {
    display: flex; gap: 12px; justify-content: flex-end; margin-top: 20px;
    .btn-cancel, .btn-primary {
      border-radius: 4px; padding: 8px 24px; font-weight: 600; border: none; cursor: pointer;
    }
    .btn-cancel { background: #8b8787; color: #fff; }
    .btn-primary { background: #3598dc; color: #fff; }
    .btn-primary:disabled { opacity: 0.5; cursor: not-allowed; }
  }
}
</style>



// WEBPACK FOOTER //
// src/vue/pages/restricted/companyDetail/configureWorkshiftAdvanced.vue